// src/components/pagamento/PaymentFormUpsell.js

import React, { useContext, useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../context/AppContext';
import { processPayment, getBoughtItems } from '../../api/vehicleAPI';
import Card from '../common/Card';
import Button from '../common/Button';
import { FaCreditCard, FaLock, FaQrcode, FaBarcode, FaWallet, FaShieldAlt, FaUserLock, FaBolt, FaSpinner } from 'react-icons/fa';
import { redirectToQRCode } from '../../utils/redirectUtils';

// ... (seus styled-components aqui, sem alteração) ...
const FormCard = styled(Card)` padding: 30px; `;
const Title = styled.h2` display: flex; align-items: center; gap: 10px; font-size: 22px; color: ${({ theme }) => theme.colors.primaryDark}; margin-bottom: 25px; `;
const PaymentMethods = styled.div` margin: 30px 0; h3 { margin-bottom: 15px; color: ${({ theme }) => theme.colors.dark}; display: flex; align-items: center; gap: 10px; } `;
const PaymentOptions = styled.div` display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; @media (max-width: 480px) { grid-template-columns: 1fr; } `;
const PaymentOption = styled.div` padding: 15px; border: 1px solid ${({ theme }) => theme.colors.lightGray}; border-radius: ${({ theme }) => theme.radii.sm}; text-align: center; cursor: pointer; transition: ${({ theme }) => theme.transitions.fast}; position: relative; &:hover { border-color: ${({ theme }) => theme.colors.primaryLight}; } &.selected { border-color: ${({ theme }) => theme.colors.primary}; background: rgba(108, 92, 231, 0.1); } &.disabled { opacity: 0.6; cursor: not-allowed; &::after { content: "Em breve"; position: absolute; top: -8px; right: -8px; background: ${({ theme }) => theme.colors.warning}; color: white; font-size: 12px; padding: 2px 8px; border-radius: ${({ theme }) => theme.radii.sm}; font-weight: bold; } } svg { font-size: 24px; margin-bottom: 10px; color: ${({ theme }) => theme.colors.primary}; } p { font-size: 14px; font-weight: 600; } `;
const InfoBoxes = styled.div` display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin: 30px 0; @media (max-width: 768px) { grid-template-columns: 1fr; } `;
const InfoBox = styled.div` background: ${({ theme }) => theme.colors.light}; border-radius: ${({ theme }) => theme.radii.md}; padding: 20px; display: flex; align-items: flex-start; gap: 15px; svg { color: ${({ theme }) => theme.colors.primary}; font-size: 24px; margin-top: 3px; } `;
const SecurePayment = styled.div` display: flex; align-items: center; justify-content: center; gap: 10px; margin-top: 20px; color: ${({ theme }) => theme.colors.gray}; font-size: 14px; svg { color: ${({ theme }) => theme.colors.success}; } `;
const spinAnimation = keyframes` from { transform: rotate(0deg); } to { transform: rotate(360deg); } `;
const SpinningIcon = styled(FaSpinner)` animation: ${spinAnimation} 1s linear infinite; `;


const PaymentFormUpsell = ({ orderDetails }) => {
  const navigate = useNavigate();
  const { cart } = useContext(AppContext);
  const [paymentMethod, setPaymentMethod] = useState('pix');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [itensComprados, setItensComprados] = useState([]);
  
  const orderId = orderDetails?.orderId;

  useEffect(() => {
    const fetchBoughtItems = async () => {
      if (!orderId) return;
      try {
        const data = await getBoughtItems(orderId);
        let boughtIds = [];
        if (Array.isArray(data) && data.length > 0 && data[0] && data[0].produto_id) {
            boughtIds = data[0].produto_id.split(',').map(id => id.trim().toUpperCase());
        }
        setItensComprados(boughtIds);
      } catch (error) {
        console.error("Erro ao buscar itens comprados no formulário:", error);
      }
    };
    fetchBoughtItems();
  }, [orderId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const itensParaPagar = cart.filter(cartItem => {
        const itemId = String(cartItem.id).toUpperCase();
        return !itensComprados.includes(itemId);
      });
      
      if (itensParaPagar.length === 0) {
        alert("Nenhum item novo foi adicionado para pagamento.");
        setIsSubmitting(false);
        return;
      }

      const payload = { 
        customer: orderDetails.customer,
        itens: itensParaPagar.map(item => item.id),
        placa: orderDetails.placa,
        orderId: orderId,
      };
      
      const result = await processPayment(payload);

      if (result.success) {
        // ==================================================================
        // LINHA CORRIGIDA
        // Adicionamos o 'orderId' no final da URL para que a próxima página
        // saiba que se trata de um upsell do pedido original.
        // ==================================================================
        redirectToQRCode(navigate, orderDetails.placa, orderId, result);
      } else {
        throw new Error(result.message || "Erro ao gerar cobrança.");
      }
    } catch (error) {
      console.error("Falha no pagamento:", error);
      alert("Erro ao processar. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormCard>
      <Title><FaCreditCard /> Finalizar Pagamento</Title>
      <form onSubmit={handleSubmit}>
        <PaymentMethods>
          <h3><FaWallet /> Método de Pagamento</h3>
          <PaymentOptions>
            <PaymentOption className="disabled"><FaCreditCard /><p>Cartão</p></PaymentOption>
            <PaymentOption className={paymentMethod === 'pix' ? 'selected' : ''}><FaQrcode /><p>PIX</p></PaymentOption>
            <PaymentOption className="disabled"><FaBarcode /><p>Boleto</p></PaymentOption>
          </PaymentOptions>
        </PaymentMethods>

        <InfoBoxes>
          <InfoBox><FaShieldAlt /><div><h4>100% Seguro</h4><p>Seus dados são criptografados.</p></div></InfoBox>
          <InfoBox><FaUserLock /><div><h4>LGPD</h4><p>Proteção de dados garantida.</p></div></InfoBox>
          <InfoBox><FaBolt /><div><h4>Entrega Rápida</h4><p>Liberação imediata após o PIX.</p></div></InfoBox>
        </InfoBoxes>

        <Button type="submit" disabled={isSubmitting || cart.length === 0} style={{ width: '100%', fontSize: '18px', padding: '16px', marginTop: '20px' }}>
          {isSubmitting ? <SpinningIcon /> : <FaLock />} {isSubmitting ? 'Processando...' : 'Pagar com PIX'}
        </Button>

        <SecurePayment>
          <FaShieldAlt /><span>Pagamento protegido</span>
        </SecurePayment>
      </form>
    </FormCard>
  );
};

export default PaymentFormUpsell;